import { motion, useMotionValue, useReducedMotion } from 'framer-motion';
import { MousePointer2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export interface MouseGlowProps {
  /** Tamanho da seta em px. */
  size?: number;
  /** Opacidade base do glow (0–1). */
  intensity?: number;
  /** Cor RGB do glow (formato `r, g, b`). */
  color?: string;
}

export function MouseGlow({ size = 22, intensity = 0.55, color = '99, 102, 241' }: MouseGlowProps) {
  const reduced = useReducedMotion();
  const x = useMotionValue<number>(-9999);
  const y = useMotionValue<number>(-9999);
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(false);

  useEffect(() => {
    if (reduced) return;

    const show = () => {
      if (!visibleRef.current) {
        visibleRef.current = true;
        setVisible(true);
      }
    };
    const hide = () => {
      if (visibleRef.current) {
        visibleRef.current = false;
        setVisible(false);
      }
    };

    const onMove = (e: MouseEvent) => {
      x.set(e.clientX);
      y.set(e.clientY);
      show();
    };
    const onLeave = () => hide();
    const onBlur = () => hide();
    const onVisibility = () => {
      if (document.hidden) hide();
    };

    window.addEventListener('mousemove', onMove);
    document.documentElement.addEventListener('mouseleave', onLeave);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('mousemove', onMove);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reduced, x, y]);

  if (reduced) return null;

  const shadow = [
    `drop-shadow(0 0 4px rgba(${color}, ${intensity}))`,
    `drop-shadow(0 0 10px rgba(${color}, ${intensity * 0.6}))`,
    `drop-shadow(0 0 18px rgba(${color}, ${intensity * 0.3}))`,
  ].join(' ');

  return (
    <motion.div
      aria-hidden="true"
      className="pointer-events-none fixed top-0 left-0 z-50"
      style={{ x, y }}
      animate={{ opacity: visible ? 1 : 0 }}
      transition={{ duration: 0.15 }}
      initial={{ opacity: 0 }}
    >
      <MousePointer2
        size={size}
        style={{
          fill: `rgba(${color}, ${intensity})`,
          stroke: 'none',
          filter: shadow,
          transform: 'translate(-3px, -3px)',
        }}
      />
    </motion.div>
  );
}
